import { api } from './client';

// ---------- Tenant ----------
export interface Tenant {
  id: string;
  name: string;
  slug: string | null;
  plan: string;
  status: string;
  max_organizations: number;
  max_users: number;
  max_documents_month: number | null;
  plan_started_at: string | null;
  plan_expires_at: string | null;
  billing_email: string | null;
  billing_phone: string | null;
}

export const tenantApi = {
  get: () => api.get<Tenant>('/tenant').then((r) => r.data),
};

// ---------- Business templates ----------
export interface BusinessTemplate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  icon: string | null;
  enabled_modules: string[];
  enabled_documents: string[] | null;
  default_settings: Record<string, unknown>;
  is_system: boolean;
  is_active: boolean;
  sort_order: number | null;
}

export interface BusinessTemplateCreate {
  name: string;
  source_template_id: string;
  description?: string | null;
}

export interface BusinessTemplatePatch {
  name?: string;
  description?: string | null;
  icon?: string | null;
  enabled_modules?: string[];
  enabled_documents?: string[];
  default_settings?: Record<string, unknown>;
}

export interface AffectedDocs {
  total: number;
  by_module: Record<string, number>;
}

export const businessTemplates = {
  list: () => api.get<BusinessTemplate[]>('/business-templates').then((r) => r.data),
  create: (payload: BusinessTemplateCreate) =>
    api.post<BusinessTemplate>('/business-templates', payload).then((r) => r.data),
  update: (id: string, payload: BusinessTemplatePatch) =>
    api.patch<BusinessTemplate>(`/business-templates/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/business-templates/${id}`).then((r) => r.data),
  affectedDocs: (id: string, modules: string[], organizationId?: string) => {
    const params = new URLSearchParams({ modules: modules.join(',') });
    if (organizationId) params.set('organization_id', organizationId);
    return api
      .get<AffectedDocs>(`/business-templates/${id}/affected-docs?${params}`)
      .then((r) => r.data);
  },
};

// ---------- Access roles ----------
export interface AccessRole {
  id: string;
  code: string;
  name: string;
  description: string | null;
  is_system: boolean;
  sort_order: number | null;
  permissions: string[];
  status: 'ACTIVE' | 'DEACTIVATED';
  created_from_role_code: string | null;
  /** ч.1119: роль лише для перегляду — права в ній не дають змінювати дані.
   *  Власна роль успадковує це від основи. */
  is_read_only?: boolean;
  /** ч.1312: межі записів за видами (код виду → межа); немає ключа — усі записи. */
  record_scopes?: Record<string, RecordScope>;
}

/** ч.1312: межа записів виду. ALL — «усі записи» (у ролі не зберігається). */
export type RecordScope = 'OWN' | 'OWN_CHAIN';

/** ч.1312: вид записів, який власна роль може звузити за автором (сервер, record_scope.py). */
export interface RecordScopeKind {
  code: string;
  label: string;
  permission_prefixes: string[];
  /** ч.1313: таблиці виду — у їхніх записів є відповідальний. */
  tables?: string[];
  /** Чи має сенс «і на їх підставі» (у документів виду є документи-підстави). */
  chain: boolean;
  /** Довідник акаунта (контрагенти): межа діє, лише коли її ставить кожна роль людини. */
  tenant_level: boolean;
  area: string;
  area_label: string;
  area_order: number;
}

export const RECORD_SCOPE_LABELS: Record<'ALL' | RecordScope, string> = {
  // ч.1317: слова власника — «тільки власні» / «всі».
  ALL: 'всі',
  OWN: 'тільки власні',
  OWN_CHAIN: 'власні і на їх підставі',
};

export interface AccessRoleCreate {
  name: string;
  source_system_role_code: string;
  permissions: string[];
  description?: string | null;
  record_scopes?: Record<string, RecordScope>;
}

export interface AccessRolePatch {
  name?: string;
  permissions?: string[];
  description?: string | null;
  /** Замінює межі записів повністю; не передано — не змінювати. */
  record_scopes?: Record<string, RecordScope>;
}

export interface RoleAssignee {
  user_id: string;
  user_email: string;
  user_full_name: string;
  organization_id: string;
  organization_name: string;
  is_active: boolean;
}

/** Один запис довідника прав (code → українська назва + модуль). Backend
 * також віддає sort_order — TS-тип його не оголошує (зайве поле нешкідливе). */
export interface PermissionCatalogItem {
  code: string;
  name: string;
  module: string;
  /** ч.1089: false — право-резерв, жодне правило його не перевіряє («поки не діє»). */
  enforced?: boolean;
  /** ч.1108: уточнення для неперевірюваних: platform / ui / reserved (інакше enforced). */
  kind?: 'enforced' | 'ui' | 'platform' | 'reserved';
  /** ч.1311: область обліку для групування в редакторі ролей (сервер, permission_areas.py). */
  area?: string;
  area_label?: string;
  area_order?: number;
  /** ч.1311: порядок дії всередині області: перегляд → створення → проведення → керування → видалення. */
  rank?: number;
}

export const accessRoles = {
  list: () => api.get<AccessRole[]>('/access-roles').then((r) => r.data),
  permissionsCatalog: () =>
    api.get<PermissionCatalogItem[]>('/access-roles/permissions-catalog').then((r) => r.data),
  /** ч.1312: види записів для меж «лише свої / і на їх підставі». */
  recordScopeKinds: () =>
    api.get<RecordScopeKind[]>('/access-roles/record-scope-kinds').then((r) => r.data),
  create: (payload: AccessRoleCreate) =>
    api.post<AccessRole>('/access-roles', payload).then((r) => r.data),
  patch: (id: string, payload: AccessRolePatch) =>
    api.patch<AccessRole>(`/access-roles/${id}`, payload).then((r) => r.data),
  deactivate: (id: string) =>
    api.post<AccessRole>(`/access-roles/${id}/deactivate`).then((r) => r.data),
  reactivate: (id: string) =>
    api.post<AccessRole>(`/access-roles/${id}/reactivate`).then((r) => r.data),
  assignees: (id: string) =>
    api.get<RoleAssignee[]>(`/access-roles/${id}/assignees`).then((r) => r.data),
};

/**
 * Канонічна множина 11 дозволених системних ролей-ОСНОВ (Variant A —
 * джерела копії для кастомної ролі). Це ПIДМНОЖИНА системних ролей: у БД
 * їх більше (16, з POS/виробничими), але як основу копіювання дозволено
 * лише ці 11.
 *
 * Віддзеркалює backend `CANONICAL_SYSTEM_ROLE_CODES` з admin.py.
 * UI використовує цю константу для майстра створення кастомної ролі.
 * Джерело істини — backend; UI лише відображає.
 *
 * Дрейф frontend↔backend ловиться grep-guard'ом (повний drift-тест —
 * у TECHDEBT після Порції Б).
 *
 * 7-й закон проекту: множина безпеки потребує тесту складу + звірки.
 */
export const CANONICAL_SYSTEM_ROLE_CODES: readonly string[] = [
  'ACCOUNTANT',
  'AUDITOR',
  'CASHIER',
  'CHIEF_ACCOUNTANT',
  'FINANCIAL_ANALYST',
  'OBSERVER',
  'PROJECT_ADMIN',
  'PURCHASER',
  'SALES_MANAGER',
  'STOREKEEPER',
  'VIEWER',
] as const;

/**
 * Захисний список 7 заборонених у кастомних ролях permissions.
 *
 * Відображає backend `CUSTOM_ROLE_FORBIDDEN_PERMISSIONS`. UI ховає ці
 * permissions у формі (зручність) — але джерело істини — backend:
 * спроба POST/PATCH з будь-яким із 7 → 422 (доведено фактом у Б.4).
 */
export const FORBIDDEN_IN_CUSTOM_ROLES: readonly string[] = [
  'admin.access.manage',
  'admin.users.manage',
  'admin.organizations.manage',
  'admin.settings.manage',
  'admin.templates.manage',
  'accounting.period.close',
  'accounting.manual.manage',
] as const;

// ч.1089: список «косметичних» прав (COSMETIC_PERMISSIONS) прибрано — він
// дублював backend і застарів (у backend резервів 10, тут було 2). Джерело
// істини — поле `enforced` у довіднику прав.

// ---------- User access ----------
export interface UserAccess {
  id: string;
  user_id: string;
  user_email: string;
  user_full_name: string;
  organization_id: string;
  organization_name: string;
  access_role_id: string;
  access_role_code: string;
  is_active: boolean;
  granted_at: string;
}

export interface UserAccessGrant {
  user_id: string;
  organization_id: string;
  access_role_id: string;
}

export const userAccess = {
  list: () => api.get<UserAccess[]>('/user-access').then((r) => r.data),
  grant: (payload: UserAccessGrant) =>
    api.post<UserAccess>('/user-access', payload).then((r) => r.data),
  revoke: (id: string) => api.delete(`/user-access/${id}`).then(() => undefined),
};

// ---------- User direction access ----------
// Обмеження користувача напрямками (kind='DIRECTION').
// Правило: пусто = всі напрямки; є записи = лише ці.
// OWNER/ADMIN ігнорують UDA (бачать усе).
export interface UserDirectionAccess {
  id: string;
  user_id: string;
  user_email: string;
  user_full_name: string;
  project_id: string;
  project_code: string;
  project_name: string;
  granted_at: string;
}

export interface UserDirectionAccessGrant {
  user_id: string;
  project_id: string;
}

export const userDirectionAccess = {
  list: () => api.get<UserDirectionAccess[]>('/user-direction-access').then((r) => r.data),
  grant: (payload: UserDirectionAccessGrant) =>
    api.post<UserDirectionAccess>('/user-direction-access', payload).then((r) => r.data),
  revoke: (id: string) =>
    api.delete(`/user-direction-access/${id}`).then(() => undefined),
};

// ---------- Users admin ----------
export interface AdminUser {
  id: string;
  full_name: string;
  email: string;
  is_active: boolean;
  tenant_role: 'OWNER' | 'ADMIN' | 'MEMBER';
  created_at: string;
}

// ч.1073: ручне створення користувача прибрано — лише запрошення
// (POST /users-admin більше немає на бекенді).
export const usersAdmin = {
  list: () => api.get<AdminUser[]>('/users-admin').then((r) => r.data),
  deactivate: (id: string) => api.delete(`/users-admin/${id}`).then(() => undefined),
};
